import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { IntermediadorPagamentoService } from '../../../../shared/services/apis/financas/intermediador-pagamento.service';
import { IntermediadorPagamento } from '../../../../shared/services/models/financas/intermediador-pagamento.model';
import { IntermediadorPagamentoLookupItem } from '../../../../shared/services/structs/financas/intermediador-pagamento-lookup-item.struct';

interface IntermediadorPagamentoItem {
    id: number;
    descricao: string;
    tipoIntermediador: string;
    idContaFinanceira: number | null;
    contaFinanceira: string;
    situacao: 'ATIVO' | 'INATIVO';
}

@Component({
    selector: 'app-intermediador-pagamento',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatCheckboxModule,
        MatMenuModule,
        MatSortModule,
        MatBadgeModule,
        MatDialogModule,
        RouterModule,
        HeaderComponent,
        EmptyListComponent,
        LoadingBoxDefaultListComponent,
        SelectPesquisavelComponent
    ],
    templateUrl: './intermediador-pagamento.component.html',
    styleUrl: './intermediador-pagamento.component.css'
})
export class IntermediadorPagamentoComponent implements OnInit {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

    public model!: FormGroup;
    public displayedColumns: string[] = ['select', 'descricao', 'tipoIntermediador', 'contaFinanceira', 'situacao', 'actions'];
    public selection = new SelectionModel<IntermediadorPagamentoItem>(true, []);
    public _dataSource = new DataSourceOrdenacaoInsensivel<IntermediadorPagamentoItem>([]);
    public _paginaAtual = 1;
    public _tamanhoPagina = 20;
    public _totalRegistros = 0;
    public _isLoading = false;
    public adquirentesDisponiveis: IntermediadorPagamentoLookupItem[] = [];

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private dialog: MatDialog,
        private intermediadorPagamentoService: IntermediadorPagamentoService
    ) { }

    ngOnInit(): void {
        this.model = this.formBuilder.group({
            pesquisa: [''],
            listIdAdquirente: [[]]
        });

        this.carregarLookup();
        this.carregarDados(true);
    }

    private carregarLookup(): void {
        this.intermediadorPagamentoService.getLookup().subscribe({
            next: (res) => {
                if (!res.isError) {
                    this.adquirentesDisponiveis = res.listaAdquirentes || [];
                }
            }
        });
    }

    public clickPesquisar(): void {
        this.carregarDados(true);
    }

    public aplicarFiltros(): void {
        this.carregarDados(true);
    }

    public limparFiltros(): void {
        this.model.patchValue({
            listIdAdquirente: []
        });
        this.carregarDados(true);
    }

    public get qtdFiltrosAvancados(): number {
        const listIdAdquirente = this.model?.get('listIdAdquirente')?.value || [];
        return listIdAdquirente.length > 0 ? 1 : 0;
    }

    public carregarDados(reset: boolean = false): void {
        this._isLoading = true;

        if (reset) {
            this._paginaAtual = 1;
            this._totalRegistros = 0;
            this.selection.clear();
            this._dataSource.data = [];
        }

        const pesquisa = (this.model.get('pesquisa')?.value || '').toString().trim();
        const listIdAdquirente = (this.model.get('listIdAdquirente')?.value || []).map((id: unknown) => Number(id));

        this.intermediadorPagamentoService.get(this._paginaAtual, this._tamanhoPagina, pesquisa, listIdAdquirente).subscribe({
            next: (res) => {
                this._isLoading = false;

                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription || 'Erro ao carregar intermediadores.', AlertType.warning);
                    return;
                }

                const items = (res.listaIntermediadorPagamento || []).map(item => this.mapItem(item));
                this._totalRegistros = res.totalRegistros || 0;

                if (this._paginaAtual === 1) {
                    this._dataSource.data = items;
                    return;
                }

                this._dataSource.data = [...this._dataSource.data, ...items];
            },
            error: () => {
                this._isLoading = false;
                this.alertService.show('Erro', 'Erro ao carregar intermediadores.', AlertType.error);
            }
        });
    }

    public proximaPagina(): void {
        if (!this.temMaisRegistros || this._isLoading) {
            return;
        }

        this._paginaAtual++;
        this.carregarDados();
    }

    public isAllSelected(): boolean {
        return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }

        this.selection.select(...this._dataSource.data);
    }

    public get temMaisRegistros(): boolean {
        return this._dataSource.data.length < this._totalRegistros;
    }

    public abrirModalExcluir(item?: IntermediadorPagamentoItem): void {
        const isBulk = !item;
        const itemsToDelete = isBulk ? this.selection.selected : [item!];

        if (!itemsToDelete.length) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: isBulk
                    ? 'Confirma a exclusão dos intermediadores selecionados?'
                    : `Confirma a exclusão do intermediador "${item!.descricao}"?`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) {
                return;
            }

            this._isLoading = true;
            const delete$ = isBulk
                ? this.intermediadorPagamentoService.bulkDelete(itemsToDelete.map(x => x.id))
                : this.intermediadorPagamentoService.delete(itemsToDelete[0].id);

            delete$.subscribe({
                next: (res) => {
                    this._isLoading = false;

                    if (res.isError) {
                        this.alertService.show('Atenção', res.errorDescription || 'Erro ao excluir intermediador.', AlertType.warning);
                        return;
                    }

                    this.selection.clear();
                    this.alertService.show(
                        'Sucesso',
                        isBulk
                            ? 'Intermediadores excluídos com sucesso.'
                            : `Intermediador "${itemsToDelete[0].descricao}" excluído com sucesso.`,
                        AlertType.success
                    );
                    this.carregarDados(true);
                },
                error: () => {
                    this._isLoading = false;
                    this.alertService.show('Erro', 'Erro ao excluir intermediador.', AlertType.error);
                }
            });
        });
    }

    private mapItem(item: IntermediadorPagamento): IntermediadorPagamentoItem {
        return {
            id: Number(item.idIntermediadorPagamento || item.id || 0),
            descricao: item.descricao,
            tipoIntermediador: item.descricaoAdquirente,
            idContaFinanceira: item.idContaFinanceira ?? null,
            contaFinanceira: item.descricaoContaFinanceira || 'Sem conta vinculada',
            situacao: item.isAtivo ? 'ATIVO' : 'INATIVO'
        };
    }
}
